import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../supabaseClient';
import './TopToolbar.css';

export default function TopToolbar({ user, saldo, onLogout }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  
  // Search States
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState({ clientes: [], cobrancas: [] });
  const [isSearching, setIsSearching] = useState(false);

  const formatCurrency = (value) => {
    const numericValue = Number(value);
    if (isNaN(numericValue)) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(0);
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numericValue);
  };

  // Fechar menu ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchModal(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Busca Global
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length > 2) {
        setIsSearching(true);
        try {
          // Buscar Clientes
          const { data: clientes } = await supabase
            .from('devedores')
            .select('id, nome, cpf_cnpj')
            .ilike('nome', `%${searchQuery}%`)
            .limit(5);

          // Buscar Cobranças
          let cobrancasResult = [];
          
          // Tratamento para busca de valor (troca vírgula por ponto)
          const valorSearch = searchQuery.replace(',', '.');
          
          // Se for numérico, tenta buscar por valor exato
          if (!isNaN(parseFloat(valorSearch))) {
             const { data } = await supabase
              .from('mensalidades')
              .select(`
                id, 
                valor, 
                data_vencimento, 
                status,
                devedores (nome)
              `)
              .eq('valor', valorSearch)
              .limit(5);
              
              if (data) cobrancasResult = [...cobrancasResult, ...data];
          }
          
          // Busca também por nosso_numero (se for texto ou número)
          const { data: cobrancasPorNumero } = await supabase
            .from('mensalidades')
            .select(`
              id, 
              valor, 
              data_vencimento, 
              status,
              devedores (nome)
            `)
            .ilike('nosso_numero', `%${searchQuery}%`)
            .limit(5);
            
          if (cobrancasPorNumero) {
             // Merge sem duplicatas
             const ids = new Set(cobrancasResult.map(c => c.id));
             cobrancasPorNumero.forEach(c => {
               if (!ids.has(c.id)) {
                 cobrancasResult.push(c);
               }
             });
          }

          setSearchResults({
            clientes: clientes || [],
            cobrancas: cobrancasResult.slice(0, 5) // Limita total de cobranças
          });
          setShowSearchModal(true);
        } catch (error) {
          console.error('Erro na busca:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults({ clientes: [], cobrancas: [] });
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleSearchFocus = () => {
    if (searchQuery.length > 0) setShowSearchModal(true);
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  return (
    <div className="top-toolbar">
      {/* Esquerda: Boas-vindas */}
      <div className="toolbar-left">
        <h1 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Bem vindo, {user?.nome?.split(' ')[0]}
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          Saldo: <strong style={{ color: '#10b981' }}>{formatCurrency(saldo)}</strong>
        </p>
      </div>

      <div style={{ flex: 1 }}></div>

      {/* Centro: Busca (Agora alinhado à direita, próximo aos ícones) */}
      <div className="toolbar-center" ref={searchRef}>
        <div className="search-bar" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-primary)' }}>
          <Icon icon="mdi:magnify" width="18" color="var(--text-secondary)" />
          <input 
            type="text" 
            placeholder="Buscar..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={handleSearchFocus}
            style={{ color: 'var(--text-primary)' }}
          />
          <div className="shortcut-badge" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-secondary)', color: 'var(--text-secondary)' }}>
            <Icon icon="mdi:apple-keyboard-command" width="10" />
            <span>K</span>
          </div>
        </div>

        {/* Modal de Resultados da Busca */}
        {showSearchModal && searchQuery.length > 2 && (
          <div className="search-results-modal">
            {isSearching ? (
              <div className="search-loading">Buscando...</div>
            ) : (
              <>
                {searchResults.clientes.length > 0 && (
                  <div className="search-section">
                    <h3 className="search-section-title">Clientes</h3>
                    {searchResults.clientes.map(cliente => (
                      <div 
                        key={cliente.id} 
                        className="search-result-item"
                        onClick={() => {
                          navigate(`/app/clientes?id=${cliente.id}`);
                          setShowSearchModal(false);
                        }}
                      >
                        <div className="result-icon bg-blue-soft color-blue">
                          <Icon icon="mdi:account" width="16" />
                        </div>
                        <div className="result-info">
                          <span className="result-title">{cliente.nome}</span>
                          <span className="result-subtitle">CPF/CNPJ: {cliente.cpf_cnpj || 'N/A'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchResults.cobrancas.length > 0 && (
                  <div className="search-section">
                    <h3 className="search-section-title">Cobranças</h3>
                    {searchResults.cobrancas.map(cobranca => (
                      <div 
                        key={cobranca.id} 
                        className="search-result-item"
                        onClick={() => {
                          // Navegar para detalhes da cobrança ou cliente
                          navigate(`/app/clientes?id=${cobranca.devedores?.id}`); // Redireciona para o cliente da cobrança por enquanto
                          setShowSearchModal(false);
                        }}
                      >
                        <div className="result-icon bg-green-soft color-green">
                          <Icon icon="mdi:cash-multiple" width="16" />
                        </div>
                        <div className="result-info">
                          <span className="result-title">{formatCurrency(cobranca.valor)} - {cobranca.devedores?.nome}</span>
                          <span className="result-subtitle">Venc: {new Date(cobranca.data_vencimento).toLocaleDateString()} • {cobranca.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {searchResults.clientes.length === 0 && searchResults.cobrancas.length === 0 && (
                  <div className="search-no-results">
                    <Icon icon="mdi:alert-circle-outline" width="24" />
                    <span>Nenhum resultado encontrado para "{searchQuery}"</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Direita: Ações e Perfil */}
      <div className="toolbar-right">
        <button 
          className="toolbar-icon-btn" 
          onClick={toggleTheme}
          title={`Mudar para modo ${theme === 'light' ? 'escuro' : 'claro'}`}
        >
          <Icon icon={theme === 'light' ? "mdi:weather-night" : "mdi:weather-sunny"} width="20" />
        </button>

        <button 
          className="toolbar-icon-btn" 
          onClick={() => navigate('/app/configuracao')}
          title="Configurações"
        >
          <Icon icon="material-symbols:settings-outline-rounded" width="20" />
        </button>

        <button 
          className="toolbar-icon-btn" 
          onClick={() => navigate('/app/help')}
          title="Central de Ajuda"
        >
          <Icon icon="mdi:help-circle-outline" width="20" />
        </button>

        <div className="profile-section" ref={menuRef}>
          <div 
            className="profile-trigger"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="Profile" />
            ) : (
              <div className="avatar-placeholder">
                {user?.nome?.charAt(0)?.toUpperCase()}
              </div>
            )}
          </div>

          {showProfileMenu && (
            <div className="profile-dropdown">
              <div className="dropdown-header">
                <span className="dropdown-user-name">{user?.nome}</span>
                <span className="dropdown-user-email">{user?.email}</span>
              </div>
              
              <button 
                className="dropdown-item"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate('/app/configuracao?aba=perfil');
                }}
              >
                <Icon icon="material-symbols:person-outline" width="18" />
                Minha Conta
              </button>
              
              <button 
                className="dropdown-item"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate('/app/configuracao?aba=planos');
                }}
              >
                <Icon icon="mdi:star-outline" width="18" />
                Planos
              </button>

              <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 0' }} />
              
              <button 
                className="dropdown-item danger"
                onClick={onLogout}
              >
                <Icon icon="mdi:logout" width="18" />
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
